import { nameIn, type StaffLanguage } from './staffText'
import type { DeliveredEntry, DeliveredOutcome, DeliveredService } from './types'

/**
 * What a desk actually delivered, under the service the customer asked for.
 *
 * The customer chose "Accounts"; the clerk closed a joint account and printed a
 * statement. Those are delivered services, with outcomes under some of them.
 * Recording one is optional: End never waits for it. See
 * internal/catalog/delivered.go.
 *
 * Every rule the Delivered panel turns on is here rather than inside Counter,
 * because the web suite runs with no DOM.
 */

/** The name a clerk reads: English, then Azerbaijani, then whatever there is, then
 *  the code — the order every other catalogue on the counter screen uses. */
export function deliveredName(d: Pick<DeliveredService | DeliveredOutcome, 'name' | 'code'>, lang: StaffLanguage): string {
  return nameIn(d.name, lang, d.code)
}

/** The outcomes a clerk may choose from for one delivered service now. */
export function offeredOutcomes(d: DeliveredService): DeliveredOutcome[] {
  return (d.outcomes ?? []).filter((o) => o.active)
}

/** Whether picking this delivered service asks how it ended. */
export function needsOutcome(d: DeliveredService): boolean {
  return offeredOutcomes(d).length > 0
}

/** The delivered services matching what the clerk has typed.
 *
 *  Every word must appear, in the code or in a name in any language, and in any
 *  order: a clerk types what they remember — "account close" — not the name as an
 *  administrator happened to write it. An empty query is everything. */
export function filterDelivered(offered: readonly DeliveredService[], query: string): DeliveredService[] {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w !== '')
  if (words.length === 0) return [...offered]
  return offered.filter((d) => {
    const haystack = [d.code, ...Object.values(d.name)].join(' ').toLowerCase()
    return words.every((w) => haystack.includes(w))
  })
}

/** An entry in words: the delivered service, and its outcome after a dot. An entry
 *  whose delivered service is no longer offered is named by its id, which is the
 *  only thing the screen still knows about it. */
export function entryLabel(entry: DeliveredEntry, offered: readonly DeliveredService[], lang: StaffLanguage): string {
  const d = offered.find((x) => x.id === entry.deliveredServiceId)
  if (!d) return entry.deliveredServiceId
  const name = deliveredName(d, lang)
  if (!entry.outcomeCode) return name
  const outcome = (d.outcomes ?? []).find((o) => o.code === entry.outcomeCode)
  return `${name} · ${outcome ? deliveredName(outcome, lang) : entry.outcomeCode}`
}

/** When an entry was added, as the clock on the wall shows it. */
export function entryTime(entry: DeliveredEntry): string {
  const at = new Date(entry.addedAt)
  if (Number.isNaN(at.getTime())) return ''
  return at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
